<template>
    <div
        class="ds-form-option-list"
        :class="{
            'ds-form-option-list-overlap': spacing === 'overlap',
            'ds-form-option-list-spaced': spacing === 'spaced',
        }"
    >
        <slot />
    </div>
</template>

<script setup lang="ts">
export interface DsCardOptionListProps {
    spacing?: 'overlap' | 'spaced';
}

withDefaults(defineProps<DsCardOptionListProps>(), {
    spacing: 'spaced',
});

defineSlots<{
    default: () => void;
}>();
</script>

<style scoped lang="scss">
.ds-form-option-list {
    @apply flex flex-col;

    &-spaced {
        @apply gap-2;
    }

    &-overlap {
        @apply relative -space-y-[5px] transition-all duration-150;

        :deep(.ds-selectable-item) {
            @apply relative;

            .ds-card-outer {
                @apply relative;
                @apply z-0 shadow-none;

                // Selected cards should pop up with higher z-index
                &.ds-card-selected {
                    @apply z-30;
                }

                &:active,
                &:focus-visible {
                    @apply z-30;
                }

                // Default: no rounded corners for middle items
                .ds-card-inner {
                    @apply rounded-none;
                }
            }
        }

        // First item: rounded top corners only
        :deep(.ds-selectable-item:first-child .ds-card-inner) {
            @apply rounded-t-lg;
        }

        :deep(.ds-selectable-item:first-child .ds-card-outer) {
            @apply rounded-t-[10px];
        }

        // Last item: rounded bottom corners only
        :deep(.ds-selectable-item:last-child .ds-card-inner) {
            @apply rounded-b-lg;
        }

        :deep(.ds-selectable-item:last-child .ds-card-outer) {
            @apply rounded-b-[10px];
        }

        // Selected items: fully rounded corners and no left/right borders
        :deep(.ds-card-outer.ds-card-selected .ds-card-inner) {
            @apply rounded-lg border-l-0 border-r-0;
        }

        // Active/focus items: fully rounded corners
        :deep(.ds-card-outer:active .ds-card-inner),
        :deep(.ds-card-outer:focus-visible .ds-card-inner) {
            @apply z-30 rounded-lg;
        }
    }
}
</style>
